const HTTP_PROTOCOL_RE = /^https?:\/\//i;

/** Checks if a string is a valid absolute HTTP URL */
export const isAbsoluteHttp = (s: string) => HTTP_PROTOCOL_RE.test(s.trim());

/** Normalizes a URL string by trimming whitespace */
export const normalizeUrl = (url: string | undefined) => (url ?? '').trim();

/** Builds an absolute URL from endpoint and optional base URL */
export const buildAbsoluteUrl = (endpoint: string, baseUrl?: string) => {
  if (isAbsoluteHttp(endpoint)) {
    return new URL(endpoint);
  }

  if (baseUrl && isAbsoluteHttp(baseUrl)) {
    // If endpoint starts with /, it would replace the base path in new URL()
    // So we need to append it to the base path instead
    const base = new URL(baseUrl);
    // Split any inline query string off the endpoint: assigning it to
    // `pathname` would percent-encode the `?` (→ `%3F`) and break routing.
    const [endpointPath, endpointQuery] = endpoint.replace(/^\/+/, '').split('?');
    const cleanBasePath = base.pathname.replace(/\/+$/, '');

    base.pathname = cleanBasePath ? `${cleanBasePath}/${endpointPath}` : `/${endpointPath}`;
    if (endpointQuery) {
      base.search = endpointQuery;
    }

    return base;
  }

  throw new Error(`Cannot build absolute URL: neither endpoint (${endpoint}) nor baseUrl (${baseUrl}) is absolute`);
};

/** Combines base path and endpoint into a single relative path */
export const buildRelativePath = (basePath: string, endpoint: string) => {
  if (!basePath) return endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  if (!endpoint) return basePath;

  const prefix = basePath.replace(/\/+$/, ''); // Remove trailing slashes
  const path = endpoint.replace(/^\/+/, ''); // Remove leading slashes

  return `${prefix}/${path}`;
};

/** Appends query parameters to a relative URL path */
export const appendQueryParams = (path: string, queryParams?: Record<string, unknown>) => {
  if (!queryParams) {
    return path;
  }

  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(queryParams)) {
    if (value === undefined || value === null) continue;

    // Convert arrays to comma-separated strings
    const stringValue = Array.isArray(value) ? value.join(',') : String(value);

    searchParams.append(key, stringValue);
  }

  const queryString = searchParams.toString();

  return queryString ? `${path}?${queryString}` : path;
};

/** Builds the final URL from endpoint, baseUrl, and query parameters */
export const buildFinalUrl = (endpoint: string, baseUrl: string | undefined, queryParams?: Record<string, unknown>) => {
  const endpointNormalized = normalizeUrl(endpoint);
  const baseUrlNormalized = normalizeUrl(baseUrl);

  const canBuildAbsolute =
    isAbsoluteHttp(endpointNormalized) || (baseUrlNormalized && isAbsoluteHttp(baseUrlNormalized));

  if (canBuildAbsolute) {
    const url = buildAbsoluteUrl(endpointNormalized, baseUrlNormalized);

    if (queryParams) {
      for (const [key, value] of Object.entries(queryParams)) {
        if (value === undefined || value === null) continue;

        // Convert arrays to comma-separated strings
        const stringValue = Array.isArray(value) ? value.join(',') : String(value);

        url.searchParams.append(key, stringValue);
      }
    }

    return url.toString();
  }

  // Relative URL case
  const relativePath = buildRelativePath(baseUrlNormalized, endpointNormalized);

  return appendQueryParams(relativePath, queryParams);
};
